/**
 * The expression language of the `AppType` descriptors (platform-catalog
 * `contracts/README.md`, « Langage d'expression »), and its templates
 * (`{expression}` placeholders in rule messages and quota `why`s).
 *
 * A SMALL HAND-WRITTEN PARSER, NO `eval`: the descriptors come from the
 * catalog, and the language is deliberately poor — field identifiers,
 * numbers, `'…'` strings, `true`/`false`, `+ - * / %`, comparisons,
 * `&& || !`, parentheses, and `min`, `max`, `floor`, `ceil`,
 * `if(c, a, b)`, `concat(a, …)`, `changed(field)`.
 *
 * THE REFERENCE is platform-catalog's `contracts/validate.py`, which
 * translates an expression to Python and evaluates it; this evaluator
 * follows Python's semantics where they can differ from JavaScript's:
 *  - precedence: `||` < `&&` < `!` < comparisons < `+ -` < `* / %` < unary
 *    minus — Python's `not` binds LOOSER than a comparison, so `!a == b` is
 *    `!(a == b)`;
 *  - `&&` / `||` return one of their operands (`a and b`), `!` a boolean;
 *  - `==` compares a boolean and a number as numbers (`True == 1`);
 *  - `%` takes the sign of the divisor (`a - b * floor(a / b)`).
 * Deliberate differences, each an error where Python would compute something
 * no descriptor needs: chained comparisons (`a < b < c`), arithmetic on
 * booleans or mixed types, a division by zero, a result that is not a
 * finite number (never written). And `if` evaluates only the branch it takes
 * (Python's translation evaluates both).
 *
 * BOUNDED, the descriptors coming from the catalog: an expression or a
 * template of at most `MAX_SOURCE_LENGTH` characters, nested at most
 * `MAX_DEPTH` deep — checked when it is parsed, i.e. when the descriptor is
 * compiled. A field is looked up among the values' OWN properties only, so
 * that a field id such as `constructor` or `toString` is a field like any
 * other, never something of `Object.prototype`.
 */

export type ExpressionValue = number | boolean | string;

export type BinaryOperator =
  | '+'
  | '-'
  | '*'
  | '/'
  | '%'
  | '=='
  | '!='
  | '<'
  | '<='
  | '>'
  | '>='
  | '&&'
  | '||';

export type FunctionName = 'min' | 'max' | 'floor' | 'ceil' | 'if' | 'concat';

export type ExpressionNode =
  | { kind: 'literal'; value: ExpressionValue }
  | { kind: 'field'; name: string }
  | { kind: 'negate'; operand: ExpressionNode }
  | { kind: 'not'; operand: ExpressionNode }
  | {
      kind: 'binary';
      op: BinaryOperator;
      left: ExpressionNode;
      right: ExpressionNode;
    }
  | { kind: 'call'; name: FunctionName; args: ExpressionNode[] }
  | { kind: 'changed'; field: string };

/** A parse or evaluation failure, in French: it reaches the actions' logs. */
export class ExpressionError extends Error {}

/** The longest expression or template accepted (header). */
export const MAX_SOURCE_LENGTH = 2000;
/** The deepest nesting accepted: parentheses, calls, unary operators. */
export const MAX_DEPTH = 64;

/** Whether `record` holds `key` itself, not through its prototype. */
export function hasOwn(record: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function tooLong(source: string): ExpressionError | undefined {
  return source.length > MAX_SOURCE_LENGTH
    ? new ExpressionError(
        `expression ou gabarit de ${source.length} caractères, au plus ${MAX_SOURCE_LENGTH}`,
      )
    : undefined;
}

type Token =
  | { kind: 'number'; text: string }
  | { kind: 'string'; text: string }
  | { kind: 'identifier'; text: string }
  | { kind: 'operator'; text: string };

// The token grammar of validate.py's TOKEN, anchored at the current position.
const TOKEN =
  /\s*(?:(\d+(?:\.\d+)?)|('[^']*')|([A-Za-z_][A-Za-z0-9_]*)|(&&|\|\||==|!=|<=|>=|[-+*/%<>!(),]))/y;

function tokenize(source: string): Token[] {
  const long = tooLong(source);
  if (long) {
    throw long;
  }
  const text = source.trimEnd();
  const tokens: Token[] = [];
  let position = 0;
  while (position < text.length) {
    TOKEN.lastIndex = position;
    const match = TOKEN.exec(text);
    if (!match || TOKEN.lastIndex === position) {
      throw new ExpressionError(
        `caractère inattendu à la position ${position} : ${JSON.stringify(
          text.slice(position),
        )}`,
      );
    }
    position = TOKEN.lastIndex;
    if (match[1] !== undefined) {
      tokens.push({ kind: 'number', text: match[1] });
    } else if (match[2] !== undefined) {
      tokens.push({ kind: 'string', text: match[2].slice(1, -1) });
    } else if (match[3] !== undefined) {
      tokens.push({ kind: 'identifier', text: match[3] });
    } else {
      tokens.push({ kind: 'operator', text: match[4] });
    }
  }
  return tokens;
}

const FUNCTIONS: ReadonlySet<string> = new Set([
  'min',
  'max',
  'floor',
  'ceil',
  'if',
  'concat',
]);

const COMPARISONS: ReadonlySet<string> = new Set([
  '==',
  '!=',
  '<',
  '<=',
  '>',
  '>=',
]);

/** The arity of each function: exact, or a minimum. */
const ARITY: Readonly<Record<FunctionName, { min: number; max?: number }>> = {
  min: { min: 1 },
  max: { min: 1 },
  floor: { min: 1, max: 1 },
  ceil: { min: 1, max: 1 },
  if: { min: 3, max: 3 },
  concat: { min: 0 },
};

class Parser {
  private index = 0;
  private depth = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly source: string,
  ) {}

  parse(): ExpressionNode {
    if (this.tokens.length === 0) {
      throw new ExpressionError('expression vide');
    }
    const node = this.or();
    if (this.index < this.tokens.length) {
      throw this.unexpected();
    }
    return node;
  }

  /** `parse` one level deeper, refused beyond `MAX_DEPTH`. */
  private nested<T>(parse: () => T): T {
    this.depth += 1;
    if (this.depth > MAX_DEPTH) {
      throw new ExpressionError(
        `expression imbriquée sur plus de ${MAX_DEPTH} niveaux dans ${JSON.stringify(
          this.source.slice(0, 80),
        )}…`,
      );
    }
    try {
      return parse();
    } finally {
      this.depth -= 1;
    }
  }

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private isOperator(text: string): boolean {
    const token = this.peek();
    return token?.kind === 'operator' && token.text === text;
  }

  private expect(text: string) {
    if (!this.isOperator(text)) {
      throw this.unexpected(`« ${text} » attendu`);
    }
    this.index += 1;
  }

  private unexpected(expected?: string): ExpressionError {
    const token = this.peek();
    const found = token ? `« ${token.text} »` : 'la fin';
    return new ExpressionError(
      `${
        expected ? `${expected}, ` : ''
      }${found} inattendu dans ${JSON.stringify(this.source)}`,
    );
  }

  private or(): ExpressionNode {
    let left = this.and();
    while (this.isOperator('||')) {
      this.index += 1;
      left = { kind: 'binary', op: '||', left, right: this.and() };
    }
    return left;
  }

  private and(): ExpressionNode {
    let left = this.not();
    while (this.isOperator('&&')) {
      this.index += 1;
      left = { kind: 'binary', op: '&&', left, right: this.not() };
    }
    return left;
  }

  private not(): ExpressionNode {
    if (this.isOperator('!')) {
      this.index += 1;
      return { kind: 'not', operand: this.nested(() => this.not()) };
    }
    return this.comparison();
  }

  private comparison(): ExpressionNode {
    const left = this.additive();
    const token = this.peek();
    if (token?.kind !== 'operator' || !COMPARISONS.has(token.text)) {
      return left;
    }
    this.index += 1;
    const right = this.additive();
    const next = this.peek();
    if (next?.kind === 'operator' && COMPARISONS.has(next.text)) {
      throw new ExpressionError(
        `comparaisons enchaînées non permises dans ${JSON.stringify(
          this.source,
        )}`,
      );
    }
    return { kind: 'binary', op: token.text as BinaryOperator, left, right };
  }

  private additive(): ExpressionNode {
    let left = this.multiplicative();
    for (;;) {
      const token = this.peek();
      if (
        token?.kind !== 'operator' ||
        (token.text !== '+' && token.text !== '-')
      ) {
        return left;
      }
      this.index += 1;
      left = {
        kind: 'binary',
        op: token.text,
        left,
        right: this.multiplicative(),
      };
    }
  }

  private multiplicative(): ExpressionNode {
    let left = this.unary();
    for (;;) {
      const token = this.peek();
      if (
        token?.kind !== 'operator' ||
        (token.text !== '*' && token.text !== '/' && token.text !== '%')
      ) {
        return left;
      }
      this.index += 1;
      left = { kind: 'binary', op: token.text, left, right: this.unary() };
    }
  }

  private unary(): ExpressionNode {
    if (this.isOperator('-')) {
      this.index += 1;
      return { kind: 'negate', operand: this.nested(() => this.unary()) };
    }
    if (this.isOperator('+')) {
      this.index += 1;
      return this.nested(() => this.unary());
    }
    return this.primary();
  }

  private primary(): ExpressionNode {
    const token = this.peek();
    if (!token) {
      throw this.unexpected('opérande attendu');
    }
    if (token.kind === 'number') {
      this.index += 1;
      const value = Number(token.text);
      if (!Number.isFinite(value)) {
        throw new ExpressionError(`nombre trop grand : ${token.text}`);
      }
      return { kind: 'literal', value };
    }
    if (token.kind === 'string') {
      this.index += 1;
      return { kind: 'literal', value: token.text };
    }
    if (token.kind === 'operator') {
      if (token.text !== '(') {
        throw this.unexpected('opérande attendu');
      }
      this.index += 1;
      const inner = this.nested(() => this.or());
      this.expect(')');
      return inner;
    }
    this.index += 1;
    const name = token.text;
    if (name === 'true' || name === 'false') {
      return { kind: 'literal', value: name === 'true' };
    }
    if (name === 'changed') {
      this.expect('(');
      const field = this.peek();
      if (field?.kind !== 'identifier' || FUNCTIONS.has(field.text)) {
        throw this.unexpected('changed() attend un champ');
      }
      this.index += 1;
      this.expect(')');
      return { kind: 'changed', field: field.text };
    }
    if (FUNCTIONS.has(name)) {
      const fn = name as FunctionName;
      this.expect('(');
      const args: ExpressionNode[] = [];
      if (!this.isOperator(')')) {
        args.push(this.nested(() => this.or()));
        while (this.isOperator(',')) {
          this.index += 1;
          args.push(this.nested(() => this.or()));
        }
      }
      this.expect(')');
      const arity = ARITY[fn];
      if (
        args.length < arity.min ||
        (arity.max !== undefined && args.length > arity.max)
      ) {
        throw new ExpressionError(
          `${fn}() reçoit ${args.length} argument(s) dans ${JSON.stringify(
            this.source,
          )}`,
        );
      }
      return { kind: 'call', name: fn, args };
    }
    return { kind: 'field', name };
  }
}

/** The expression `source`, parsed; throws an `ExpressionError`. */
export function parseExpression(source: string): ExpressionNode {
  return new Parser(tokenize(source), source).parse();
}

/** The fields an expression reads, `changed()`'s argument included. */
export function expressionFields(node: ExpressionNode): Set<string> {
  const found = new Set<string>();
  const visit = (item: ExpressionNode) => {
    switch (item.kind) {
      case 'field':
        found.add(item.name);
        return;
      case 'changed':
        found.add(item.field);
        return;
      case 'negate':
      case 'not':
        visit(item.operand);
        return;
      case 'binary':
        visit(item.left);
        visit(item.right);
        return;
      case 'call':
        item.args.forEach(visit);
        return;
      default:
        return;
    }
  };
  visit(node);
  return found;
}

/** Whether an expression calls `changed()`. */
export function usesChanged(node: ExpressionNode): boolean {
  switch (node.kind) {
    case 'changed':
      return true;
    case 'negate':
    case 'not':
      return usesChanged(node.operand);
    case 'binary':
      return usesChanged(node.left) || usesChanged(node.right);
    case 'call':
      return node.args.some(usesChanged);
    default:
      return false;
  }
}

/** What an evaluation reads. */
export interface EvaluationContext {
  values: Readonly<Record<string, ExpressionValue>>;
  /**
   * `changed(field)`, in the sense of the phase evaluating (README:
   * « demandé » for an adjustment, « modifié » otherwise). Absent where the
   * descriptor may not call it (quota): a call is then an error.
   */
  changed?: (field: string) => boolean;
}

/** Python's truth value of the reference's operands. */
export function truthy(value: ExpressionValue): boolean {
  return value !== false && value !== 0 && value !== '';
}

function describe(value: ExpressionValue): string {
  return typeof value === 'string' ? `'${value}'` : String(value);
}

function number(value: ExpressionValue, where: string): number {
  if (typeof value !== 'number') {
    throw new ExpressionError(`${where} : nombre attendu, ${describe(value)}`);
  }
  return value;
}

function equal(a: ExpressionValue, b: ExpressionValue): boolean {
  if (typeof a === 'string' || typeof b === 'string') {
    return a === b;
  }
  // Python's bool is an int: True == 1.
  return Number(a) === Number(b);
}

/**
 * A value as `concat()` and the templates write it: a string as is, a whole
 * number in digits, a boolean `true`/`false` (validate.py `as_text`).
 */
export function asText(value: ExpressionValue): string {
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'number') {
    if (!Number.isInteger(value)) {
      throw new ExpressionError(`nombre non entier dans un texte : ${value}`);
    }
    return String(value);
  }
  return value;
}

function binary(
  op: BinaryOperator,
  left: ExpressionValue,
  right: ExpressionValue,
): ExpressionValue {
  switch (op) {
    case '+':
      if (typeof left === 'string' && typeof right === 'string') {
        return left + right;
      }
      return number(left, '+') + number(right, '+');
    case '-':
      return number(left, '-') - number(right, '-');
    case '*':
      return number(left, '*') * number(right, '*');
    case '/': {
      const divisor = number(right, '/');
      if (divisor === 0) {
        throw new ExpressionError('division par zéro');
      }
      return number(left, '/') / divisor;
    }
    case '%': {
      const divisor = number(right, '%');
      if (divisor === 0) {
        throw new ExpressionError('division par zéro');
      }
      const dividend = number(left, '%');
      return dividend - divisor * Math.floor(dividend / divisor);
    }
    case '==':
      return equal(left, right);
    case '!=':
      return !equal(left, right);
    default: {
      // < <= > >=: two numbers or two strings.
      if (typeof left === 'string' && typeof right === 'string') {
        return compare(op, orderOf(left, right));
      }
      return compare(op, orderOf(number(left, op), number(right, op)));
    }
  }
}

/** -1, 0 or 1 as `a` sorts before, with or after `b`. */
function orderOf<T extends number | string>(a: T, b: T): number {
  if (a < b) return -1;
  return a > b ? 1 : 0;
}

function compare(op: BinaryOperator, order: number): boolean {
  switch (op) {
    case '<':
      return order < 0;
    case '<=':
      return order <= 0;
    case '>':
      return order > 0;
    default:
      return order >= 0;
  }
}

/**
 * The value of `node` over `context`; throws an `ExpressionError`, a result
 * that is not a finite number included.
 */
export function evaluate(
  node: ExpressionNode,
  context: EvaluationContext,
): ExpressionValue {
  const value = evaluateNode(node, context);
  if (typeof value === 'number' && !Number.isFinite(value)) {
    throw new ExpressionError(`résultat non fini (${value})`);
  }
  return value;
}

function evaluateNode(
  node: ExpressionNode,
  context: EvaluationContext,
): ExpressionValue {
  switch (node.kind) {
    case 'literal':
      return node.value;
    case 'field': {
      const value = hasOwn(context.values, node.name)
        ? context.values[node.name]
        : undefined;
      if (value === undefined) {
        throw new ExpressionError(
          `champ inconnu ou sans valeur : ${node.name}`,
        );
      }
      return value;
    }
    case 'changed':
      if (!context.changed) {
        throw new ExpressionError('changed() n’a pas de sens ici');
      }
      return context.changed(node.field);
    case 'negate':
      return -number(evaluate(node.operand, context), '-');
    case 'not':
      return !truthy(evaluate(node.operand, context));
    case 'binary': {
      const left = evaluate(node.left, context);
      if (node.op === '&&') {
        return truthy(left) ? evaluate(node.right, context) : left;
      }
      if (node.op === '||') {
        return truthy(left) ? left : evaluate(node.right, context);
      }
      return binary(node.op, left, evaluate(node.right, context));
    }
    case 'call': {
      if (node.name === 'if') {
        const [condition, then, otherwise] = node.args;
        return evaluate(
          truthy(evaluate(condition, context)) ? then : otherwise,
          context,
        );
      }
      const args = node.args.map(arg => evaluate(arg, context));
      switch (node.name) {
        case 'concat':
          return args.map(asText).join('');
        case 'floor':
          return Math.floor(number(args[0], 'floor'));
        case 'ceil':
          return Math.ceil(number(args[0], 'ceil'));
        case 'min':
          return Math.min(...args.map(arg => number(arg, 'min')));
        default:
          return Math.max(...args.map(arg => number(arg, 'max')));
      }
    }
    default:
      throw new ExpressionError('expression inconnue');
  }
}

/** A template: its literal parts and its placeholders, in order. */
export type Template = ReadonlyArray<string | ExpressionNode>;

const PLACEHOLDER = /\{([^{}]*)\}/g;

/**
 * `text` as a template: each `{expression}` parsed, a lone brace or an empty
 * placeholder an error (validate.py `template_expressions`).
 */
export function parseTemplate(text: string): Template {
  const long = tooLong(text);
  if (long) {
    throw long;
  }
  const parts: Array<string | ExpressionNode> = [];
  let last = 0;
  for (const match of text.matchAll(PLACEHOLDER)) {
    const index = match.index ?? 0;
    parts.push(text.slice(last, index));
    if (match[1].trim() === '') {
      throw new ExpressionError(`gabarit vide dans ${JSON.stringify(text)}`);
    }
    parts.push(parseExpression(match[1]));
    last = index + match[0].length;
  }
  parts.push(text.slice(last));
  if (
    parts.some(
      part =>
        typeof part === 'string' && (part.includes('{') || part.includes('}')),
    )
  ) {
    throw new ExpressionError(
      `accolade non appariée dans ${JSON.stringify(text)}`,
    );
  }
  return parts.filter(part => part !== '');
}

/** The fields a template reads. */
export function templateFields(template: Template): Set<string> {
  const found = new Set<string>();
  for (const part of template) {
    if (typeof part !== 'string') {
      expressionFields(part).forEach(field => found.add(field));
    }
  }
  return found;
}

/** Whether a template calls `changed()`. */
export function templateUsesChanged(template: Template): boolean {
  return template.some(part => typeof part !== 'string' && usesChanged(part));
}

/** The template's text over `context`, each value written by `asText`. */
export function renderTemplate(
  template: Template,
  context: EvaluationContext,
): string {
  return template
    .map(part =>
      typeof part === 'string' ? part : asText(evaluate(part, context)),
    )
    .join('');
}
